<script setup>
import AutocompleteFilter from '@/components/widgets/AutocompleteFilter.vue';
import { inject, ref, onMounted } from 'vue';
import ShipperForm from '@/components/Customer/ShipperForm.vue';
import { useInfinite } from '@/composables/use-infinity';

const backendApi = inject('backendApi');

const emit = defineEmits(['update', 'update:focused', 'update:modelValue']);

defineProps({
    hideLabel: {
        type: Boolean,
        default: false
    },
    density: { type: String, default: '' },
    hideDetails: { default: '' },
    rows: { default: '' },
    errorMessages: { default: '' },
    variant: { default: '' },
    readonly: { default: '' },
    color: { default: '' },
    class: { default: '' },
    is: { default: '' },
    returnObject: { default: '' },
    modelValue: { default: '' },
    clearable: { default: true },
    fieldModels: Array,
    label: String,
    multiple: Boolean,
    disabled: Boolean,
    documentType: String
});

const fetchCustomers = ({ pattern, page, limit }) =>
    backendApi().customers().index({
        pattern,
        active: true,
        page,
        limit
    });

const {
    items: customers,
    loading,
    showRetryButton,
    hasMore,
    searchQuery,
    getItems,
    loadMore,
    reset
} = useInfinite(fetchCustomers);

const selected = ref(null);
const shipperEditorRef = ref(null);

const onCustomerPressed = () => {
    shipperEditorRef.value.open();
};

onMounted(() => getItems());

const save = async (customer) => {
    emit('update', customer);
    emit('update:modelValue', customer);
};

const onUpdate = () => {
    emit('update', selected.value);
};

const onClearPressed = () => {
    searchQuery.value = '';
    selected.value = null;
    emit('update', selected.value);
    reset();
    getItems();
};
</script>

<template>
    <AutocompleteFilter
        @update:modelValue="
            (val) => {
                emit('update:modelValue', val);
            }
        "
        :modelValue="modelValue"
        v-model:search="searchQuery"
        :items="customers"
        :is="is"
        :hide-details="hideDetails"
        :density="density"
        :rows="rows"
        :error-messages="errorMessages"
        :variant="variant"
        :readonly="readonly"
        :color="color"
        :return-object="returnObject"
        item-title="number"
        item-value="id"
        :label="hideLabel ? '' : $t('Customer')"
        :loading="loading"
        :show-retry-button="showRetryButton"
        :infinite-scroll="hasMore"
        @infinite-scroll="loadMore"
        @update="onUpdate"
        @preparing-search="
            () => {
                reset();
                getItems();
            }
        "
        @search="
            () => {
                reset();
                getItems();
            }
        "
        @retry="
            () => {
                reset();
                getItems();
            }
        "
        @click:clear="onClearPressed"
        @update:focused="(state) => emit('update:focused', state)"
        :clearable="clearable"
    >
        <template v-slot:default="{ props, item }">
            <v-list-item
                v-bind="props"
                :title="`${item.raw.name} ${item.raw.lastname ? item.raw.lastname : ''}`"
                :subtitle="`${item.raw.email ? item.raw.email : ''}`"
            ></v-list-item>
        </template>
        <template v-if="$slots.prepend" v-slot:prepend>
            <slot name="prepend"></slot>
        </template>
        <template v-slot:prepend-item>
            <v-btn
                color="primary"
                variant="tonal"
                class="btnNewCustomer"
                prepend-icon="mdi-account-plus-outline"
                @click="onCustomerPressed"
            >
                {{ $t('Create new customer') }}
            </v-btn>
        </template>
    </AutocompleteFilter>

    <ShipperForm
        ref="shipperEditorRef"
        :title-page="$t('Packages')"
        @update="save"
    ></ShipperForm>
</template>

<style lang="scss" scoped>
.btnNewCustomer {
    margin-left: 12px;
    margin-right: auto;
}
</style>
